import { AxiosResponse } from 'axios'

interface OidcTokenResponse {
  access_token: string
  refresh_token: string
  id_token?: string
  expires_in: number
}

const STATE_KEY = 'ff_oidc_state'
const VERIFIER_KEY = 'ff_oidc_verifier'
const NONCE_KEY = 'ff_oidc_nonce'

function issuer(): string {
  return (process.env.VUE_APP_OIDC_ISSUER_URL || '').replace(/\/$/, '')
}

function clientId(): string {
  return process.env.VUE_APP_OIDC_CLIENT_ID || ''
}

function randomValue(): string {
  const bytes = new Uint8Array(32)
  crypto.getRandomValues(bytes)
  return base64Url(bytes)
}

function base64Url(value: Uint8Array): string {
  let binary = ''
  value.forEach((byte) => (binary += String.fromCharCode(byte)))
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

async function sha256(value: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(value),
  )
  return base64Url(new Uint8Array(digest))
}

function tokenPayload(token: string): Record<string, unknown> {
  const encoded = token.split('.')[1]
  if (!encoded) throw new Error('OIDC token is malformed.')
  const normalized = encoded.replace(/-/g, '+').replace(/_/g, '/')
  return JSON.parse(
    atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=')),
  )
}

function redirectUri(): string {
  return `${window.location.origin}/`
}

async function tokenRequest(
  params: Record<string, string>,
): Promise<OidcTokenResponse> {
  const response = await fetch(`${issuer()}/protocol/openid-connect/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({ client_id: clientId(), ...params }),
  })
  if (!response.ok)
    throw new Error(`OIDC token exchange failed (${response.status}).`)
  return response.json()
}

export default abstract class OidcApi {
  public static configured(): boolean {
    return Boolean(issuer() && clientId())
  }

  public static callbackPending(): boolean {
    const query = new URLSearchParams(window.location.search)
    return query.has('code') || query.has('error')
  }

  public static async start(): Promise<void> {
    if (!this.configured()) throw new Error('OIDC is not configured.')
    const state = randomValue()
    const verifier = randomValue()
    const nonce = randomValue()
    sessionStorage.setItem(STATE_KEY, state)
    sessionStorage.setItem(VERIFIER_KEY, verifier)
    sessionStorage.setItem(NONCE_KEY, nonce)

    const query = new URLSearchParams({
      client_id: clientId(),
      redirect_uri: redirectUri(),
      response_type: 'code',
      scope: 'openid',
      state,
      nonce,
      code_challenge: await sha256(verifier),
      code_challenge_method: 'S256',
    })
    window.location.assign(`${issuer()}/protocol/openid-connect/auth?${query}`)
  }

  public static async finish(): Promise<OidcTokenResponse> {
    const query = new URLSearchParams(window.location.search)
    if (query.get('error'))
      throw new Error('Keycloak login was cancelled or failed.')
    const code = query.get('code')
    const state = query.get('state')
    const expectedState = sessionStorage.getItem(STATE_KEY)
    const verifier = sessionStorage.getItem(VERIFIER_KEY)
    const expectedNonce = sessionStorage.getItem(NONCE_KEY)
    sessionStorage.removeItem(STATE_KEY)
    sessionStorage.removeItem(VERIFIER_KEY)
    sessionStorage.removeItem(NONCE_KEY)
    if (
      !code ||
      !state ||
      !expectedState ||
      state !== expectedState ||
      !verifier
    ) {
      throw new Error('Keycloak login response could not be verified.')
    }

    const tokens = await tokenRequest({
      grant_type: 'authorization_code',
      code,
      redirect_uri: redirectUri(),
      code_verifier: verifier,
    })
    if (tokens.id_token && expectedNonce) {
      const nonce = tokenPayload(tokens.id_token).nonce
      if (nonce !== expectedNonce)
        throw new Error('Keycloak login nonce did not match.')
    }
    window.history.replaceState({}, document.title, '/')
    return tokens
  }

  public static async refreshToken(
    refreshToken: string,
  ): Promise<AxiosResponse> {
    const data = await tokenRequest({
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
    })
    return { data } as AxiosResponse
  }

  public static logoutUrl(): string | null {
    if (!this.configured()) return null
    const query = new URLSearchParams({
      client_id: clientId(),
      post_logout_redirect_uri: redirectUri(),
    })
    const idToken = localStorage.getItem('id_token')
    if (idToken) query.set('id_token_hint', idToken)
    return `${issuer()}/protocol/openid-connect/logout?${query}`
  }

  public static usernameFromToken(token: string): string {
    const payload = tokenPayload(token)
    return String(payload.preferred_username || payload.username || '')
  }
}
